import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  CalendarClock,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Plus,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelect } from '@/components/ui/multi-select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { UsersApi } from '@/services/users/usersApi'
import {
  ReportsApi,
  absoluteReportUrl,
  type Recurrence,
  type ReportPeriod,
  type ReportSchedule,
  type ReportScheduleInput,
  type WeekDay,
} from '@/services/reports/reportsApi'
import { MAX_DAY_OF_MONTH, WEEK_DAYS, nextRunText } from '@/services/reports/reportCadence'
import { ReportTemplatesApi, type ReportRef } from '@/services/reports/reportTemplatesApi'
import {
  ALL_ACTIVE_FLAG,
  REPORT_TYPES,
  hasChildTemplate,
  type ReportType,
} from '@/services/reports/reportTypes'
import { formatDate } from '@/utils/format'
import { AppShell } from '../../components/AppShell'
import { CurrencySelect } from '../../components/CurrencySelect'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { SearchSelect } from '../../components/SearchSelect'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { TokenInput } from '../../components/TokenInput'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { CopyReportItemDialog } from '../../components/reports/CopyReportItemDialog'
import { ReportObjectSelect } from '../../components/reports/ReportObjectSelect'
import { useCurrentUser } from '../../contexts/UserContext'
import { useNetwork } from '../../contexts/NetworkContext'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

const LIST_PATH = '/reports/schedules'

const PERIODS: ReportPeriod[] = [
  'RECURRENCE_PERIOD',
  'FLIGHT',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'CURRENT_MONTH',
]

/** How often a schedule of this report type can run — as the old UI offers it. */
function recurrencesFor(reportType: string, current: Recurrence | null): Recurrence[] {
  const offered: Recurrence[] = ['DAILY', 'WEEKLY', 'MONTHLY']
  if (reportType === 'SELF_SERVICE') offered.push('ONCE')
  if (reportType === 'LINE_ITEM' || reportType === 'SELF_SERVICE') offered.push('END_OF_FLIGHT')
  // A schedule set up elsewhere keeps its own recurrence on offer.
  if (current && !offered.includes(current)) offered.push(current)
  return offered
}

/** Runs on a fixed calendar beat, so a time and day mean something. */
const isCalendarRecurrence = (r: Recurrence) => r === 'DAILY' || r === 'WEEKLY' || r === 'MONTHLY'

// ── Inner form component ────────────────────────────────────────────────────

interface ReportScheduleEditFormProps {
  schedule: ReportSchedule | null
  id: string
  isNew: boolean
}

function ReportScheduleEditForm({ schedule, id, isNew }: ReportScheduleEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()
  const [copyOpen, setCopyOpen] = useState(false)
  const timeZone = schedule?.timeZone || currentNetwork?.timeZone || 'UTC'

  const form = useForm({
    defaultValues: {
      name: schedule?.name ?? '',
      templateId: schedule?.templateId ?? '',
      childTemplateId: schedule?.childTemplateId ?? '',
      target: (schedule?.target ?? null) as ReportRef | null,
      allAdvertisers: schedule?.parameters?.[ALL_ACTIVE_FLAG] === true ||
        schedule?.parameters?.[ALL_ACTIVE_FLAG] === 'true',
      recurrence: (schedule?.recurrence ?? 'WEEKLY') as Recurrence,
      period: (schedule?.period ?? 'RECURRENCE_PERIOD') as ReportPeriod,
      periodTime: schedule?.periodTime ?? '',
      periodDayOfWeek: (schedule?.periodDayOfWeek ?? 'MONDAY') as WeekDay,
      periodDayOfMonth: schedule?.periodDayOfMonth ?? 1,
      recipientUserIds: schedule?.recipientUsers.map((u) => u.id) ?? [],
      recipientEmailAddresses: schedule?.recipientEmailAddresses ?? [],
      currency:
        schedule?.currency ||
        currentUser?.preferredCurrency ||
        currentNetwork?.defaultCurrency ||
        '',
    },
    onSubmit: ({ value }) => {
      const reportType = templateType(value.templateId) as ReportType
      const definition = REPORT_TYPES[reportType]
      // Every object parameter is rebuilt from the form, so switching the
      // template's type never leaves the old object behind.
      const objectKeys = new Set([
        ...Object.values(REPORT_TYPES).map((rt) => rt.idKey),
        'lineItemIds',
        'relatedLineItem',
        ALL_ACTIVE_FLAG,
      ])
      const parameters: Record<string, unknown> = Object.fromEntries(
        Object.entries(schedule?.parameters ?? {}).filter(([key]) => !objectKeys.has(key))
      )
      if (reportType === 'ADVERTISER' && value.allAdvertisers) parameters[ALL_ACTIVE_FLAG] = true
      else if (reportType === 'NETWORK') parameters[definition.idKey] = currentNetwork?.id
      else if (definition && value.target) parameters[definition.idKey] = value.target.id

      const calendar = isCalendarRecurrence(value.recurrence)
      mutation.mutate({
        name: value.name,
        reportTemplate: { id: value.templateId },
        childReportTemplate:
          hasChildTemplate(reportType) && value.childTemplateId
            ? { id: value.childTemplateId }
            : null,
        recurrence: value.recurrence,
        period: value.recurrence === 'END_OF_FLIGHT' ? 'FLIGHT' : value.period,
        periodTime: calendar && value.periodTime ? value.periodTime : null,
        periodDayOfWeek: value.recurrence === 'WEEKLY' ? value.periodDayOfWeek : null,
        periodDayOfMonth: value.recurrence === 'MONTHLY' ? value.periodDayOfMonth : null,
        timeZone,
        currency: value.currency || undefined,
        locale: schedule?.locale || currentUser?.locale || undefined,
        parameters,
        recipientUsers: value.recipientUserIds.map((userId) => ({ id: userId })),
        recipientEmailAddresses: value.recipientEmailAddresses,
        objectState: (schedule?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => ReportsApi.saveSchedule(id, { objectState }),
    ['report-schedule', id],
    ['report-schedules']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)
  const values = useStore(form.store, (s) => s.values)
  const set = form.setFieldValue

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['report-templates', 'all'],
    queryFn: () => ReportTemplatesApi.getAll(),
    staleTime: 60 * 1000,
  })
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'report-recipients'],
    queryFn: () => UsersApi.getAll({ minimal: true }),
    staleTime: 5 * 60 * 1000,
  })

  function templateType(templateId: string): string {
    if (templateId === schedule?.templateId && !templates.some((tpl) => tpl.id === templateId)) {
      // Not among the active templates — fall back to what the schedule already reports on.
      return reportTypeFromParameters(schedule?.parameters ?? {})
    }
    return templates.find((tpl) => tpl.id === templateId)?.reportType ?? ''
  }

  const template = templates.find((tpl) => tpl.id === values.templateId)
  const reportType = templateType(values.templateId) as ReportType | ''
  const definition = reportType ? REPORT_TYPES[reportType] : undefined
  const childTemplates = templates.filter((tpl) => tpl.reportType === 'LINE_ITEM')
  const recurrences = recurrencesFor(reportType, schedule?.recurrence ?? null)
  const periods = PERIODS.filter((p) => p !== 'FLIGHT' || definition?.hasFlight)
  const needsTarget =
    !!definition?.searchType && !(reportType === 'ADVERTISER' && values.allAdvertisers)
  const availableCurrencies = currentNetwork?.availableCurrencies ?? []
  const userOptions = (users?.results ?? []).map((user) => ({
    label: user.displayName || user.name || user.username,
    value: user.id,
  }))

  const summary = t('reports.summarySentence', {
    when: t(`reports.when.${values.recurrence}`, {
      time: values.periodTime || '00:00',
      day: t(`reports.days.${values.periodDayOfWeek}`),
      dayOfMonth: values.periodDayOfMonth,
    }),
    covers: t(`reports.covers.${values.recurrence === 'END_OF_FLIGHT' ? 'FLIGHT' : values.period}`, {
      recurrence: t(`reports.every.${values.recurrence}`),
    }),
    timeZone,
  })

  const changeTemplate = (templateId: string) => {
    const previous = reportType
    set('templateId', templateId)
    const next = templates.find((tpl) => tpl.id === templateId)?.reportType ?? ''
    if (next === previous) return
    // A different kind of report needs a different object, and may not run as often.
    set('target', null)
    set('childTemplateId', '')
    if (!recurrencesFor(next, null).includes(values.recurrence)) set('recurrence', 'WEEKLY')
    if (values.period === 'FLIGHT' && !REPORT_TYPES[next as ReportType]?.hasFlight) {
      set('period', 'RECURRENCE_PERIOD')
    }
  }

  const mutation = useMutation({
    mutationFn: (input: ReportScheduleInput) => ReportsApi.saveSchedule(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['report-schedule', id], data)
      queryClient.invalidateQueries({ queryKey: ['report-schedules'] })
      markClean()
      if (isNew) navigate(`${LIST_PATH}/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('reports.scheduleCreated') : t('reports.scheduleSaved'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('reportSchedules.createFailed')
            : t('reportSchedules.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('reportSchedules.title'), href: LIST_PATH },
    {
      label: isNew
        ? t('reportSchedules.newSchedule')
        : (schedule?.name ?? t('reportSchedules.editTitle')),
    },
  ]

  return (
    <AppShell
      crumbs={crumbs}
      showBack
      objectType={isNew ? undefined : t('objectTypes.reportSchedule')}
    >
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={schedule?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.reportSchedule')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('reportSchedules.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <Button type="button" variant="outline" onClick={() => setCopyOpen(true)}>
                <Copy className="size-3.5" />
                {t('reportsModule.copy')}
              </Button>
              <ObjectStateToggle value={schedule!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {schedule && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={schedule.id} /> },
              { label: t('reportSchedules.nextRun'), content: nextRunText(schedule, t) },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(schedule.createTime)}
                    updated={formatDate(schedule.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={FileText} />
              {t('reportSchedules.whatToReport')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="field-grid">
              <div className="p-field full">
                <FieldLabel hint={template?.description || undefined}>
                  {t('reports.template')}
                </FieldLabel>
                <form.Field
                  name="templateId"
                  validators={{
                    onSubmit: ({ value }) =>
                      value ? undefined : t('reportSchedules.templateRequired'),
                  }}
                >
                  {(field) => (
                    <>
                      <SearchSelect
                        value={field.state.value}
                        onChange={changeTemplate}
                        options={templates.map((tpl) => ({
                          value: tpl.id,
                          label: `${tpl.name} · ${t(`reportTypes.${tpl.reportType}.simple`, { defaultValue: tpl.reportType })}`,
                        }))}
                        placeholder={t('reports.templatePlaceholder')}
                        fallbackLabel={schedule?.templateName}
                        loading={templatesLoading}
                      />
                      {field.state.meta.errors[0] && (
                        <p className="text-xs text-destructive">
                          {field.state.meta.errors[0] as string}
                        </p>
                      )}
                    </>
                  )}
                </form.Field>
              </div>

              {reportType === 'ADVERTISER' && (
                <div className="p-field full">
                  <RadioGroup
                    value={values.allAdvertisers ? 'all' : 'one'}
                    onValueChange={(v) => set('allAdvertisers', v === 'all')}
                    className="flex flex-wrap gap-4"
                  >
                    <Label className="font-normal">
                      <RadioGroupItem value="one" />
                      {t('reportSchedules.oneAdvertiser')}
                    </Label>
                    <Label className="font-normal">
                      <RadioGroupItem value="all" />
                      {t('reportSchedules.allAdvertisers')}
                    </Label>
                  </RadioGroup>
                </div>
              )}

              {needsTarget && definition?.searchType && (
                <div className="p-field">
                  <FieldLabel>{t(`reportTypes.${reportType}.object`)}</FieldLabel>
                  <form.Field
                    name="target"
                    validators={{
                      onSubmit: ({ value }) =>
                        needsTarget && !value ? t('reportSchedules.targetRequired') : undefined,
                    }}
                  >
                    {(field) => (
                      <>
                        <ReportObjectSelect
                          key={reportType}
                          searchType={definition.searchType!}
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v)}
                          placeholder={t('reportsModule.selectObject')}
                          invalid={field.state.meta.errors.length > 0}
                        />
                        {field.state.meta.errors[0] && (
                          <p className="text-xs text-destructive">
                            {field.state.meta.errors[0] as string}
                          </p>
                        )}
                      </>
                    )}
                  </form.Field>
                </div>
              )}

              {hasChildTemplate(reportType) && (
                <div className="p-field">
                  <FieldLabel tooltip={t('reportsModule.childTemplateHelp')}>
                    {t('reportsModule.childTemplate')}
                  </FieldLabel>
                  <SearchSelect
                    value={values.childTemplateId}
                    onChange={(v) => set('childTemplateId', v)}
                    options={childTemplates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
                    placeholder={t('reportsModule.noChildTemplate')}
                    fallbackLabel={schedule?.childTemplateName}
                    clearable
                  />
                </div>
              )}

              {availableCurrencies.length > 1 && (
                <div className="p-field">
                  <FieldLabel>{t('reports.currency')}</FieldLabel>
                  <CurrencySelect value={values.currency} onValueChange={(v) => set('currency', v)} />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={CalendarClock} />
              {t('reportSchedules.whenToRun')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="field-grid field-grid--3-responsive">
                <div className="p-field">
                  <FieldLabel tooltip={t('reports.recurrenceHelp')}>
                    {t('reports.recurrenceLabel')}
                  </FieldLabel>
                  <Select
                    value={values.recurrence}
                    onValueChange={(v) => set('recurrence', v as Recurrence)}
                  >
                    <SelectTrigger>
                      <SelectValue>{t(`reports.recurrence.${values.recurrence}`)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {recurrences.map((r) => (
                        <SelectItem key={r} value={r}>
                          {t(`reports.recurrence.${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {values.recurrence === 'WEEKLY' && (
                  <div className="p-field">
                    <FieldLabel>{t('reports.dayOfWeek')}</FieldLabel>
                    <Select
                      value={values.periodDayOfWeek}
                      onValueChange={(v) => set('periodDayOfWeek', v as WeekDay)}
                    >
                      <SelectTrigger>
                        <SelectValue>{t(`reports.days.${values.periodDayOfWeek}`)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {WEEK_DAYS.map((day) => (
                          <SelectItem key={day} value={day}>
                            {t(`reports.days.${day}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {values.recurrence === 'MONTHLY' && (
                  <div className="p-field">
                    <FieldLabel hint={t('reports.dayOfMonthHint')}>{t('reports.dayOfMonth')}</FieldLabel>
                    <Input
                      type="number"
                      min={1}
                      max={MAX_DAY_OF_MONTH}
                      value={values.periodDayOfMonth}
                      onChange={(e) =>
                        set(
                          'periodDayOfMonth',
                          Math.min(Math.max(Number(e.target.value) || 1, 1), MAX_DAY_OF_MONTH)
                        )
                      }
                    />
                  </div>
                )}
                {isCalendarRecurrence(values.recurrence) && (
                  <div className="p-field">
                    <FieldLabel hint={t('reportSchedules.timeDefaultHint')}>
                      {t('reports.timeOfDay')}
                    </FieldLabel>
                    <Input
                      type="time"
                      value={values.periodTime}
                      onChange={(e) => set('periodTime', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {values.recurrence !== 'END_OF_FLIGHT' && (
                <div className="p-field">
                  <FieldLabel tooltip={t('reports.periodHelp')}>{t('reports.coverage')}</FieldLabel>
                  <RadioGroup
                    value={values.period}
                    onValueChange={(v) => set('period', v as ReportPeriod)}
                    className="flex flex-col gap-2"
                  >
                    {periods.map((p) => (
                      <Label key={p} className="font-normal">
                        <RadioGroupItem value={p} />
                        {t(`reportSchedules.periods.${p}`)}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <p className="flex items-start gap-2 rounded-(--ad-radius) bg-(--ad-neutral-soft) p-2.5 text-xs text-(--ad-text-muted)">
                <CalendarClock className="mt-px size-3.5 shrink-0" />
                {summary}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Mail} />
              {t('reportSchedules.delivery')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="field-grid">
              <div className="p-field full">
                <FieldLabel tooltip={t('reports.recipientsHelp')}>
                  {t('reportSchedules.recipients')}
                </FieldLabel>
                <MultiSelect
                  options={userOptions}
                  value={values.recipientUserIds}
                  onChange={(v) => set('recipientUserIds', v)}
                  placeholder={
                    usersLoading ? t('common.loading') : t('reports.recipientsPlaceholder')
                  }
                />
              </div>
              <div className="p-field full">
                <FieldLabel tooltip={t('reports.emailRecipientsHelp')}>
                  {t('reports.emailRecipients')}
                </FieldLabel>
                <TokenInput
                  value={values.recipientEmailAddresses}
                  onChange={(v) => set('recipientEmailAddresses', v)}
                  lowercase
                  placeholder={t('reports.emailPlaceholder')}
                />
              </div>
              {!isNew && (
                <div className="p-field full">
                  <FieldLabel tooltip={t('reportSchedules.latestReportUrlHelp')}>
                    {t('reportSchedules.latestReportUrl')}
                  </FieldLabel>
                  {schedule?.reportUrl ? (
                    <a
                      href={absoluteReportUrl(schedule.reportUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 text-sm break-all text-(--ad-link)"
                    >
                      {absoluteReportUrl(schedule.reportUrl)}
                      <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('reportSchedules.urlNotYetAvailable')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(LIST_PATH)}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('reportSchedules.newSchedule') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      {schedule && copyOpen && (
        <CopyReportItemDialog
          open={copyOpen}
          onOpenChange={setCopyOpen}
          kind={t('objectTypes.reportSchedule')}
          source={schedule}
          withDescription={false}
          copy={(newId, name) => ReportsApi.copySchedule(schedule, newId, name)}
          listKey="report-schedules"
          pathFor={(newId) => `${LIST_PATH}/${newId}`}
        />
      )}
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

/** The report type a schedule's parameters point at, for a template that is no longer listed. */
function reportTypeFromParameters(parameters: Record<string, unknown>): string {
  if (parameters[ALL_ACTIVE_FLAG]) return 'ADVERTISER'
  return (
    Object.values(REPORT_TYPES).find((rt) => rt.id !== 'SELF_SERVICE' && parameters[rt.idKey])?.id ??
    ''
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function ReportScheduleEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: schedule,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['report-schedule', id],
    queryFn: () => ReportsApi.getSchedule(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('reportSchedules.title'), href: LIST_PATH },
    { label: isNew ? t('reportSchedules.newSchedule') : t('reportSchedules.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return <ReportScheduleEditForm key={id} schedule={schedule ?? null} id={id!} isNew={isNew} />
}
